import DOMPurify from 'dompurify';

/**
 * XSS Sanitizer Service
 * Provides methods to sanitize HTML content to prevent XSS attacks
 */
class XssSanitizerService {
  /**
   * Default configuration for DOMPurify
   */
  static defaultConfig = {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'table', 'thead', 'tbody', 'tr', 'td', 'th',
      'a', 'img',
    ],
    ALLOWED_ATTR: [
      'href', 'src', 'alt', 'title', 'class', 'id', 'style',
      'target', 'rel',
    ],
    ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
    KEEP_CONTENT: true,
    RETURN_DOM: false,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM_IMPORT: false,
    RETURN_TRUSTED_TYPE: false,
    FORCE_BODY: false,
    SANITIZE_DOM: true,
    WHOLE_DOCUMENT: false,
    ADD_URI_SAFE_ATTR: ['target', 'rel'],
  };

  /**
   * Sanitize HTML content with default configuration
   * @param {string} html - HTML content to sanitize
   * @returns {string} - Sanitized HTML content
   */
  static sanitize (html) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    try {
      return DOMPurify.sanitize(html);
    } catch (error) {
      console.error('Error sanitizing HTML:', error);
      return this.escapeHtmlWithLineBreaks(html);
    }
  }

  /**
   * Sanitize HTML content with custom configuration
   * @param {string} html - HTML content to sanitize
   * @param {object} config - Custom DOMPurify configuration
   * @returns {string} - Sanitized HTML content
   */
  static sanitizeWithConfig (html, config = {}) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    try {
      const mergedConfig = { ...this.defaultConfig, ...config };
      return DOMPurify.sanitize(html, mergedConfig);
    } catch (error) {
      console.error('Error sanitizing HTML with custom config:', error);
      return this.escapeHtmlWithLineBreaks(html);
    }
  }

  /**
   * Sanitize HTML content for rich text (allows more HTML tags)
   * @param {string} html - HTML content to sanitize
   * @returns {string} - Sanitized HTML content
   */
  static sanitizeRichText (html) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    const richTextConfig = {
      ...this.defaultConfig,
      ALLOWED_TAGS: [
        ...this.defaultConfig.ALLOWED_TAGS,
        'blockquote', 'pre', 'code', 'mark', 'del', 'ins',
        'sub', 'sup', 'small', 'big', 'cite', 'q', 'abbr',
        'acronym', 'dfn', 'kbd', 'samp', 'var', 'time',
      ],
      ALLOWED_ATTR: [
        ...this.defaultConfig.ALLOWED_ATTR,
        'cite', 'datetime', 'dir', 'lang',
      ],
    };

    return this.sanitizeWithConfig(html, richTextConfig);
  }

  /**
   * Sanitize HTML content for simple text (only basic formatting)
   * @param {string} html - HTML content to sanitize
   * @returns {string} - Sanitized HTML content
   */
  static sanitizeSimpleText (html) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    const simpleTextConfig = {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span'],
      ALLOWED_ATTR: ['class', 'style'],
      KEEP_CONTENT: true,
    };

    return this.sanitizeWithConfig(html, simpleTextConfig);
  }

  /**
   * Escape HTML content and preserve line breaks
   * @param {string} text - Text to escape
   * @returns {string} - Escaped text with preserved line breaks
   */
  static escapeHtmlWithLineBreaks (text) {
    if (!text || typeof text !== 'string') {
      return '';
    }

    const map = {
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      '\'': '&#039;',
    };

    // Escape HTML and convert line breaks to <br> tags
    return text
      .replace(/[&<>"']/g, (m) => map[m])
      .replace(/\n/g, '<br>')
      .replace(/\r\n/g, '<br>');
  }

  /**
   * Check if content contains potentially dangerous HTML
   * @param {string} html - HTML content to check
   * @returns {boolean} - True if content is potentially dangerous
   */
  static isPotentiallyDangerous (html) {
    if (!html || typeof html !== 'string') {
      return false;
    }

    const dangerousPatterns = [
      /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi,
      /<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi,
      /<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi,
      /<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi,
    ];

    return dangerousPatterns.some(pattern => pattern.test(html));
  }

  /**
   * Get sanitized content with fallback to escaped content if dangerous
   * @param {string} html - HTML content to process
   * @param {object} config - DOMPurify configuration
   * @returns {string} - Safe content
   */
  static getSafeContent (html, config = null) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    // If content is potentially dangerous, escape it completely
    if (this.isPotentiallyDangerous(html)) {
      return this.escapeHtmlWithLineBreaks(html);
    }

    // Otherwise sanitize with provided config or default
    return config ? this.sanitizeWithConfig(html, config) : this.sanitize(html);
  }

  /**
   * Light sanitization for already sanitized content (just in case)
   * This is a minimal sanitization for content that should already be safe
   * @param {string} html - HTML content to lightly sanitize
   * @returns {string} - Lightly sanitized HTML content
   */
  static lightSanitize (html) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    // Only remove the most dangerous patterns
    const lightConfig = {
      ALLOWED_TAGS: ['*'],
      ALLOWED_ATTR: ['*'],
      FORBID_TAGS: ['script', 'iframe', 'object', 'embed'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
      KEEP_CONTENT: true,
    };

    try {
      return DOMPurify.sanitize(html, lightConfig);
    } catch (error) {
      console.error('Error in light sanitization:', error);
      return this.escapeHtmlWithLineBreaks(html);
    }
  }

  /**
   * Render content safely - use this for display when you're not sure if content is sanitized
   * @param {string} html - HTML content to render safely
   * @param {boolean} trustSource - Whether to trust that content is already sanitized
   * @returns {string} - Safe content for rendering
   */
  static renderSafely (html, trustSource = false) {
    if (!html || typeof html !== 'string') {
      return '';
    }

    if (trustSource) {
      // If we trust the source (e.g., content created through our sanitized input),
      // just do light sanitization
      return this.lightSanitize(html);
    } else {
      // If we don't trust the source, do full sanitization
      return this.sanitize(html);
    }
  }
}

export default XssSanitizerService;
